import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { JurisdictionSwitcherModal } from './JurisdictionSwitcherModal';
import { Icon, type IconName } from './Icon';
import { PoweredByBadge } from './PoweredByBadge';
import { ThreeThirtyLogo } from './ThreeThirtyLogo';


interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: string;
  permission?: string;
  description?: string;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    group: 'Command Center',
    items: [
      { to: '/', label: 'Overview & Dashboard', icon: 'dashboard', description: 'Real-time KPIs, attendance punch & role overview' },
      { to: '/audit', label: 'Security & Audit Trail', icon: 'audit', permission: 'audit.view', description: 'Append-only ledger of sensitive HR changes & access logs' },
    ],
  },
  {
    group: 'Workforce & Talent',
    items: [
      { to: '/employees', label: 'People Directory', icon: 'employees', permission: 'employee.view', description: 'Workforce roster, employee profiles & organization tree' },
      { to: '/attendance', label: 'Attendance & Shifts', icon: 'attendance', permission: 'attendance.clock', description: 'Daily clock-in/out, shift assignments & timesheets' },
      { to: '/leaves', label: 'Leave & Time Off', icon: 'leaves', permission: 'leave.apply', description: 'Leave balances, quota policies & absence approvals' },
      { to: '/recruitment', label: 'Careers & Recruitment', icon: 'recruitment', permission: 'recruitment.view', description: 'Job openings, internal postings & hiring pipeline' },
      { to: '/documents', label: 'Letters & Document Vault', icon: 'fileText', permission: 'document.manage', description: 'Universal template builder, dynamic merge & document vault' },
      { to: '/grievances', label: 'Ethics & Grievance Hub', icon: 'shieldCheck', permission: 'grievance.submit', description: 'Confidential employee grievances, hearings & POSH desk' },
      { to: '/exit', label: 'Separation & Clearances', icon: 'exit', permission: 'exit.view', description: 'Resignations, notice tracking & full-and-final settlements' },
    ],
  },
  {
    group: 'Compensation & Benefits',
    items: [
      { to: '/payroll', label: 'Payroll & Payslips', icon: 'payroll', permission: 'payroll.view', description: 'Monthly payroll runs, statutory filings & compensation' },
      { to: '/expenses', label: 'Expense Claims', icon: 'expenses', permission: 'expense.submit', description: 'Employee expense claims, receipt uploads & approval tracker' },
      { to: '/loans', label: 'Loans & Salary Advances', icon: 'loans', permission: 'loan.apply', description: 'Salary advances, loan repayment schedules & EMI tracking' },
    ],
  },
  {
    group: 'Finance & Treasury',
    items: [
      { to: '/finance', label: 'Finance, Invoices & P&L', icon: 'finance', permission: 'finance.view', description: 'General ledger, bank/cash accounts, AP/AR invoices & budgets' },
    ],
  },
  {
    group: 'Equipment & IT',
    items: [
      { to: '/assets', label: 'Asset Custody & Inventory', icon: 'assets', permission: 'asset.manage', description: 'Hardware inventory, device allocations & maintenance' },
    ],
  },
  {
    group: 'Administration & System',
    items: [
      { to: '/departments', label: 'Departments & Hierarchy', icon: 'departments', permission: 'department.view', description: 'Organizational hierarchy, cost centers & budget assignments' },
      { to: '/access-control', label: 'Roles & Access Governance', icon: 'shieldCheck', permission: 'org.manage', description: 'Assign persona roles, manage internal user accounts & permissions' },
      { to: '/bulk-import', label: 'Bulk Data Ingestion', icon: 'database', permission: 'org.manage', description: 'Batch upload employees, attendance, invoices, assets & candidates via CSV/JSON' },
    ],
  },
];


export interface RolePersona {
  key: string;
  badge: string;
  icon: IconName;
  name: string;
  description: string;
  email: string;
  color: string;
}

export function getRolePersona(roles: string[]): RolePersona {
  if (roles.includes('organization_owner') || roles.includes('owner')) {
    return {
      key: 'owner',
      badge: 'Executive Suite',
      icon: 'award',
      name: 'Owner & CEO',
      description: 'Full enterprise visibility, treasury governance & system control',
      email: 'owner@abny.in',
      color: '#b38b4d',
    };
  }
  if (roles.includes('finance_manager') || roles.includes('finance')) {
    return {
      key: 'finance',
      badge: 'Financial Control',
      icon: 'bank',
      name: 'Finance Manager',
      description: 'General ledger, AP/AR, cash balances, budgets & vendor settlements',
      email: 'finance@abny.test',
      color: '#1b3d2c',
    };
  }
  if (roles.includes('hr_admin') || roles.includes('hr')) {
    return {
      key: 'hr',
      badge: 'HR & Talent Admin',
      icon: 'users',
      name: 'HR Administrator',
      description: 'Workforce directory, recruitment ATS, policy compliance & life-cycle',
      email: 'hr@abny.test',
      color: '#be123c',
    };
  }
  if (roles.includes('payroll_admin') || roles.includes('payroll')) {
    return {
      key: 'payroll',
      badge: 'Payroll Controller',
      icon: 'cash',
      name: 'Payroll Admin',
      description: 'Salary structures, payroll runs, statutory compliance & payslips',
      email: 'payroll@abny.test',
      color: '#0f766e',
    };
  }
  if (roles.includes('manager')) {
    return {
      key: 'manager',
      badge: 'Team Leader',
      icon: 'userCheck',
      name: 'Team Manager (Asha Menon)',
      description: 'Team attendance oversight, leave approvals, expense reviews',
      email: 'asha@abny.test',
      color: '#4338ca',
    };
  }
  return {
    key: 'employee',
    badge: 'Staff Portal (ESS)',
    icon: 'smartphone',
    name: 'Staff / Employee (Rohit Verma)',
    description: 'Personal punch clock, leave requests, expense submissions & payslips',
    email: 'rohit@abny.test',
    color: '#0284c7',
  };
}

export const ALL_PERSONAS: RolePersona[] = [
  {
    key: 'owner',
    badge: 'Executive Suite',
    icon: 'award',
    name: 'Owner & CEO',
    description: 'Full enterprise visibility, treasury governance & system control',
    email: 'owner@abny.in',
    color: '#b38b4d',
  },
  {
    key: 'finance',
    badge: 'Financial Control',
    icon: 'bank',
    name: 'Finance Manager',
    description: 'General ledger, AP/AR, cash balances, budgets & settlements',
    email: 'finance@abny.test',
    color: '#1b3d2c',
  },
  {
    key: 'payroll',
    badge: 'Payroll Controller',
    icon: 'cash',
    name: 'Payroll Admin',
    description: 'Salary structures, payroll runs & statutory compliance',
    email: 'payroll@abny.test',
    color: '#0f766e',
  },
  {
    key: 'hr',
    badge: 'HR & Talent Admin',
    icon: 'users',
    name: 'HR Administrator',
    description: 'Workforce directory, recruitment ATS & policy compliance',
    email: 'hr@abny.test',
    color: '#be123c',
  },
  {
    key: 'manager',
    badge: 'Team Leader',
    icon: 'userCheck',
    name: 'Team Manager (Asha)',
    description: 'Team attendance oversight & operational approvals',
    email: 'asha@abny.test',
    color: '#4338ca',
  },
  {
    key: 'employee',
    badge: 'Staff Portal (ESS)',
    icon: 'smartphone',
    name: 'Staff / Employee (Rohit)',
    description: 'Personal punch clock, leave requests & payslips',
    email: 'rohit@abny.test',
    color: '#0284c7',
  },
];


export function Shell() {
  const { session, signIn, signOut, can } = useAuth();
  const { jurisdiction } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [commandPalette, setCommandPalette] = useState(false);
  const [roleSwitcherModal, setRoleSwitcherModal] = useState(false);
  const [jurisdictionModal, setJurisdictionModal] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Global Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandPalette((prev) => !prev);
      } else if (e.key === 'Escape' && (commandPalette || roleSwitcherModal || jurisdictionModal)) {
        setCommandPalette(false);
        setRoleSwitcherModal(false);
        setJurisdictionModal(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPalette, roleSwitcherModal, jurisdictionModal]);


  if (!session) return null;

  const currentPersona = getRolePersona(session.user.roles);

  // Filter navigation items by active user permissions
  const filteredGroups = NAV_GROUPS.map((g) => ({
    group: g.group,
    items: g.items.filter((item) => !item.permission || can(item.permission)),
  })).filter((g) => g.items.length > 0);

  // Flat list of all accessible searchable actions/routes
  const allSearchable = filteredGroups.flatMap((g) =>
    g.items.map((item) => ({ ...item, group: g.group }))
  );

  const filteredSearch = allSearchable.filter(
    (item) =>
      item.label.toLowerCase().includes(paletteQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(paletteQuery.toLowerCase())) ||
      item.group.toLowerCase().includes(paletteQuery.toLowerCase())
  );

  // Derive breadcrumb from current path
  const currentPath = location.pathname;
  let pageTitle = 'Executive Overview';
  for (const g of NAV_GROUPS) {
    for (const it of g.items) {
      if (it.to === currentPath || (it.to !== '/' && currentPath.startsWith(it.to))) {
        pageTitle = it.label;
        break;
      }
    }
  }

  function handleNavigate(to: string) {
    navigate(to);
    setCommandPalette(false);
    setMobileMenuOpen(false);
    setPaletteQuery('');
  }

  async function handleSwitchPersona(targetEmail: string) {
    setSwitchingRole(true);
    try {
      await signIn(session?.tenant.slug || 'abny', targetEmail, 'ChangeMe!2026');
      setRoleSwitcherModal(false);
      navigate('/');
    } catch (err) {
      console.error('Failed to switch persona:', err);
    } finally {
      setSwitchingRole(false);
    }
  }

  return (
    <div className={`shell role-theme-${currentPersona.key}`}>
      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Modern Enterprise Sidebar (Matching Reference Design) */}
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-top-bar">
          <div className="sidebar-window-dots">
            <span className="window-dot red" />
            <span className="window-dot yellow" />
            <span className="window-dot green" />
          </div>
          <button
            className="sidebar-search-btn"
            onClick={() => setCommandPalette(true)}
            aria-label="Search modules (⌘K)"
            title="Search (⌘K)"
          >
            <Icon name="search" size="xs" />
          </button>
          <button
            className="sidebar-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <Icon name="close" size="sm" />
          </button>
        </div>

        <div className="sidebar-header">
          <div className="brand-logo">
            <div className="brand-symbol">
              <ThreeThirtyLogo size={18} variant="badge" />
            </div>
            <span>THREE THIRTY</span>
            <span className="version-pill">LLC</span>
          </div>
        </div>


        {/* Segmented Persona Switcher Pill (Matching Reference) */}
        <div className="sidebar-segmented-switcher">
          <button
            type="button"
            className={`segmented-btn ${currentPersona.key === 'employee' ? 'active' : ''}`}
            onClick={() => void handleSwitchPersona('rohit@abny.test')}
            title="Switch to Personal / Employee Self-Service"
          >
            Personal
          </button>
          <button
            type="button"
            className={`segmented-btn ${currentPersona.key !== 'employee' ? 'active' : ''}`}
            onClick={() => {
              if (currentPersona.key === 'employee') {
                void handleSwitchPersona('owner@abny.in');
              } else {
                setRoleSwitcherModal(true);
              }
            }}
            title="Switch to Business / Governance Admin"
          >
            Business
          </button>
        </div>

        <nav className="nav-container">
          {filteredGroups.map((g) => (
            <div key={g.group} className="nav-group">
              <div className="nav-group-title">{g.group}</div>
              <div className="nav-group-items">
                {g.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                  >
                    <Icon name={item.icon} size="md" className="nav-icon" />
                    <span className="nav-item-label">{item.label}</span>
                    {item.badge && <span className="nav-item-badge">{item.badge}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Powered By ABNY Web in Sidebar */}
        <PoweredByBadge position="sidebar-dock" />

        {/* Floating Capsule User Profile Card (Matching Reference) */}
        <div className="sidebar-profile-card">
          <div className="sidebar-profile-info" onClick={() => setRoleSwitcherModal(true)} title="Click to switch role">
            <div className="sidebar-profile-avatar">
              <Icon name={currentPersona.icon} size="sm" />
            </div>
            <div className="sidebar-profile-text">
              <strong className="sidebar-profile-name">{session.user.fullName}</strong>
              <span className="sidebar-profile-email">{session.user.email}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button
              className="sidebar-profile-more"
              onClick={() => setRoleSwitcherModal(true)}
              aria-label="Switch Persona or options"
              title="Switch Persona (Role Switcher)"
            >
              <Icon name="arrowUpDown" size="xs" />
            </button>
            <button
              className="sidebar-profile-more"
              onClick={() => void signOut()}
              aria-label="Sign out"
              title="Sign out"
            >
              <Icon name="exit" size="xs" />
            </button>
          </div>
        </div>
      </aside>


      {/* Main Content Area with Top Contextual Header */}
      <div className="main-wrapper">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="btn-icon mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Icon name="menu" size="md" />
            </button>
            <div className="topbar-breadcrumbs">
              <span className="crumb-org">{session.tenant.name.split(' ')[0]}</span>
              <span className="crumb-sep">/</span>
              <span className="crumb-current">{pageTitle}</span>
            </div>
          </div>

          <div className="topbar-actions">
            {/* Desktop Full Search Bar */}
            <div className="global-search-wrapper desktop-only-search" onClick={() => setCommandPalette(true)} style={{ cursor: 'pointer' }}>
              <Icon name="search" size="sm" className="search-icon" />
              <span style={{ color: 'var(--ink-muted)', fontSize: 13, flex: 1, padding: '4px 0' }}>
                Search & jump (⌘K)...
              </span>
              <span className="kbd-shortcut">⌘K</span>
            </div>

            {/* Mobile Compact Search Button */}
            <button
              className="btn-icon mobile-search-btn"
              onClick={() => setCommandPalette(true)}
              aria-label="Search"
              title="Search modules (⌘K)"
            >
              <Icon name="search" size="sm" />
            </button>

            {/* Prominent Role Persona Badge & Instant Switcher */}
            <button
              className="role-persona-badge"
              onClick={() => setRoleSwitcherModal(true)}
              title="Current Active Role — Click to Switch Persona"
            >
              <span className="role-persona-indicator">●</span>
              <Icon name={currentPersona.icon} size="xs" />
              <span className="role-persona-badge-text">{currentPersona.badge}</span>
              <span className="role-persona-badge-short">{currentPersona.name.split(' ')[0]}</span>
              <Icon name="arrowUpDown" size="xs" style={{ opacity: 0.6, marginLeft: 2 }} />
            </button>

            {/* International Country & Jurisdiction Switcher Pill */}
            <button
              className="jurisdiction-pill-badge"
              onClick={() => setJurisdictionModal(true)}
              title={`Operating under ${jurisdiction.countryName} (${jurisdiction.currency}) — Click to change accounting jurisdiction`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 11px',
                background: 'var(--bg-field)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-full)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                color: 'var(--ink)',
                transition: 'all 150ms ease',
              }}
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>{jurisdiction.flag}</span>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>
                {jurisdiction.countryCode} · {jurisdiction.currency}
              </span>
              <Icon name="chevronDown" size="xs" style={{ opacity: 0.6 }} />
            </button>
          </div>
        </header>


        <main className="main">
          <Outlet />
        </main>

        {/* Dynamic Expanding Floating Capsule Bottom Navigation Dock (Matching Reference Image 2) */}
        <nav className="mobile-bottom-nav">
          <div className="mobile-nav-pill-container">
            <NavLink to="/" end className={({ isActive }) => (isActive ? 'mobile-nav-item active' : 'mobile-nav-item')}>
              <Icon name="dashboard" size="md" className="mobile-nav-icon" />
              <span className="mobile-nav-label">Dashboard</span>
            </NavLink>
            <NavLink to="/attendance" className={({ isActive }) => (isActive ? 'mobile-nav-item active' : 'mobile-nav-item')}>
              <Icon name="attendance" size="md" className="mobile-nav-icon" />
              <span className="mobile-nav-label">Punch</span>
            </NavLink>
            <NavLink to="/leaves" className={({ isActive }) => (isActive ? 'mobile-nav-item active' : 'mobile-nav-item')}>
              <Icon name="leaves" size="md" className="mobile-nav-icon" />
              <span className="mobile-nav-label">Leaves</span>
            </NavLink>
            <NavLink to="/payroll" className={({ isActive }) => (isActive ? 'mobile-nav-item active' : 'mobile-nav-item')}>
              <Icon name="payroll" size="md" className="mobile-nav-icon" />
              <span className="mobile-nav-label">Payroll</span>
            </NavLink>
            <button
              type="button"
              className="mobile-nav-item"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open full navigation drawer"
            >
              <Icon name="menu" size="md" className="mobile-nav-icon" />
              <span className="mobile-nav-label">Menu</span>
            </button>
          </div>
        </nav>
      </div>


      {/* Interactive Command Palette Modal (Cmd+K) */}
      {commandPalette && (
        <div className="modal-backdrop" onClick={() => setCommandPalette(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 560, padding: 0, overflow: 'hidden', borderRadius: 10 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon name="search" size="md" />
              <input
                type="text"
                autoFocus
                placeholder="Type a module name or action (e.g. Finance, Payroll, Leaves)..."
                value={paletteQuery}
                onChange={(e) => {
                  setPaletteQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                style={{
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  fontSize: 14,
                  padding: 0,
                  background: 'transparent',
                }}
              />
              <span className="kbd-shortcut" style={{ fontSize: 11 }}>ESC</span>
            </div>

            <div style={{ maxHeight: 340, overflowY: 'auto', padding: 8 }}>
              {filteredSearch.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-muted)', fontSize: 13 }}>
                  No modules match "<strong>{paletteQuery}</strong>"
                </div>
              ) : (
                filteredSearch.map((item, idx) => (
                  <div
                    key={item.to}
                    onClick={() => handleNavigate(item.to)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: idx === selectedIndex ? 'var(--bg-field)' : 'transparent',
                      transition: 'background 120ms ease',
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: 'var(--brand-light)',
                        color: 'var(--brand-primary)',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <Icon name={item.icon} size="sm" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{item.label}</div>
                      {item.description && (
                        <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 1 }}>{item.description}</div>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--ink-muted)', background: 'var(--bg-field)', padding: '2px 6px', borderRadius: 4 }}>
                      {item.group}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Role Switcher Persona Modal */}
      {roleSwitcherModal && (
        <div className="modal-backdrop" onClick={() => setRoleSwitcherModal(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 700, padding: '24px 28px', borderRadius: 16 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <span className="eyebrow">
                  <Icon name="award" size="xs" />
                  <span>Enterprise Identity & RBAC</span>
                </span>
                <h3>Switch Persona & Role View</h3>
              </div>
              <button className="btn-icon" onClick={() => setRoleSwitcherModal(false)} aria-label="Close modal">
                <Icon name="close" size="sm" />
              </button>
            </div>

            <p className="muted" style={{ fontSize: 13, marginBottom: 18, lineHeight: 1.45 }}>
              Experience the application from different organizational perspectives with real role permissions, dashboard intelligence, and workflows:
            </p>

            <div className="role-switcher-grid">
              {ALL_PERSONAS.map((p) => {
                const isSelected = p.key === currentPersona.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    className={`role-switcher-card ${isSelected ? 'active' : ''}`}
                    onClick={() => void handleSwitchPersona(p.email)}
                    disabled={switchingRole}
                    style={{
                      borderLeft: isSelected ? `4px solid ${p.color}` : '1px solid var(--border-subtle)',
                    }}
                  >
                    <div
                      className="role-switcher-icon"
                      style={{
                        background: isSelected ? 'var(--brand-primary)' : 'var(--bg-field)',
                        color: isSelected ? '#fff' : p.color,
                      }}
                    >
                      <Icon name={p.icon} size="md" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>{p.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 3, lineHeight: 1.35, wordBreak: 'normal', overflowWrap: 'break-word' }}>
                        {p.description}
                      </div>
                      <span
                        className="role-tag-pill"
                        style={{
                          background: isSelected ? 'var(--brand-light)' : 'var(--bg-field)',
                          color: isSelected ? 'var(--brand-primary)' : 'var(--ink-muted)',
                        }}
                      >
                        {p.badge}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {switchingRole && (
              <div style={{ textAlign: 'center', marginTop: 16, color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600 }}>
                Authenticating & reloading persona workspace...
              </div>
            )}

            {/* Corporate Registration Info */}
            <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: 11, color: 'var(--ink-muted)' }}>
              <div>
                <strong style={{ color: 'var(--ink-secondary)' }}>THREE THIRTY LLC</strong> · Birmingham, AL 35242
              </div>
              <a
                href="https://threethirtyllc.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <span>threethirtyllc.com</span>
                <Icon name="externalLink" size="xs" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Global Country & Accounting Jurisdiction Switcher Modal */}
      <JurisdictionSwitcherModal
        isOpen={jurisdictionModal}
        onClose={() => setJurisdictionModal(false)}
      />
    </div>
  );
}


